import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Input, Card, message, Modal, Pagination } from 'antd';
import { Plus, Search, Edit2, Trash2, Package, Filter, RefreshCw } from 'lucide-react';
import Header from '../../components/header';
import ProductForm from './ProductForm';
import SimpleProductFilter from './SimpleProductFilter';
import { getAllProducts, deleteProduct } from '../../services/productService';
import {
  useOrganizationSettings,
  formatCurrency,
  OrganizationSettings
} from '../../utils/i18nUtils';
import { useSelector } from 'react-redux';
import BulkProductImport from './BulkProductImport';
import logger from '../../utils/logger';
import EmptyState from '../../components/EmptyState';

interface ProductImage {
  bucketUrl: any;
  filePath: string;
  fileName: string;
  uploadedBy: string;
  uploadedAt: string;
  fileSize: string;
}

interface Product {
  unit: JSX.Element;
  _id: string;
  name: string;
  description: string | null;
  images: ProductImage[];
  currency: string;
  price: number;
  category: {
    _id: string;
    name: string;
  };
  extraDetails: {
    unit?: string;
    [key: string]: any;
  };
  isActive: boolean;
  createdAt: string;
}

interface ApiResponse {
  data: Product[];
  total: number;
}

// Styled Components preserving class names
const StyledMainContainer = styled.div.attrs({ className: 'main-container' })`
  padding: 16px;
  background-color: #f5f7f9;
  min-height: calc(100vh - 64px);
    font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, sans-serif;

`;

const ContentHeader = styled.div.attrs({ className: 'content-header' })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  .title-section {
    display: flex;
    align-items: center;
    gap: 8px;

    h1 {
      font-size: 20px;
      font-weight: 600;
      color: #1e293b;
      margin: 0;
      font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, sans-serif;
    }
  }

  .actions-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const SearchContainer = styled.div.attrs({ className: 'search-wrapper' })`
  position: relative;
  width: 450px;
  margin-right: 8px;

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #8392ab;
    pointer-events: none;
    z-index: 1;
  }

  .ant-input {
    width: 100%;
    padding-left: 36px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    height: 40px;
    
    &::placeholder {
      color: #94a3b8;
    }
    
    &:focus {
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }
  }
`;

const TruncatedProductName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #1e293b;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  
  /* Text truncation - limits to 2 lines with ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 2.8em; /* Approximately 2 lines of text */
  line-height: 1.4;
`;

const TruncatedProductDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  
  /* Text truncation - limits to 1 line with ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const StyledInput = styled(Input)`
  padding: 8px 12px 8px 36px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.875rem;
  height: 38px;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  padding: 12px;
  grid-auto-rows: minmax(200px, auto);  /* Increase minimum height */
`;

const ProductCard = styled(Card)`
  border-radius: 8px;
  background: white;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  
  .ant-card-body {
    padding: 8px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    .action-buttons {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const ActionButtons = styled.div.attrs({ className: 'action-buttons' })`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  background: white;
  padding: 4px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

const Badge = styled.span`
  background: #f1f5f9;
  color: #64748b;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
`;

const StyledButton = styled(Button)`
  &.btn-primary {
    background: #1890ff;
    border-color: #1890ff;
    color: white;
    height: 40px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-weight: 500;
    font-size: 14px;
    border-radius: 6px;
    box-shadow: 0 2px 0 rgba(0, 0, 0, 0.045);

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background: #40a9ff;
      border-color: #40a9ff;
    }

    &:active {
      background: #096dd9;
      border-color: #096dd9;
    }
  }
`;

interface StatusBadgeProps {
  status?: string;
}

const StatusBadge = styled.div<StatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'active': return '#4CB050';
      case 'inactive': return '#E32C2C';
      default: return '#8392ab';
    }
  }};
`;

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductForm, setShowProductForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState<any>(null);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const pageSize = 12;


 

  const bucketUrl = useSelector((state: any) => 
    state.rootReducers?.userData?.userDetails?.bucketUrl || ''
  );
  logger.log('bucketUrl from Redux:', bucketUrl);

  const { 
    currency: currencyCode, 
    locale 
  }: OrganizationSettings = useOrganizationSettings();
  logger.log('ProductList currency settings:', { currencyCode, locale });
  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, appliedFilters]);

  useEffect(() => {
    // Reset to page 1 when search term changes
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setShowProductForm(true);
  };

  const handleDelete = async (confirm: boolean) => {
    if (confirm && selectedProduct) {
      try {
        await deleteProduct(selectedProduct._id);
        message.success('Product deleted successfully');
        fetchProducts();
      } catch (error) {
        message.error('Error deleting product');
      }
    }
    setDeleteModalVisible(false);
    setSelectedProduct(null);
  };

  const handleApplyFilter = (filterData: any) => {
    logger.log('Applying product filter:', filterData);
    setAppliedFilters(filterData);
    setCurrentPage(1); // Reset to first page when filters change
    
    // Check if there are any active filters
    const hasFilters = filterData.search || 
                      filterData.category || 
                      (filterData.customFieldFilter && filterData.customFieldFilter.length > 0);
    setHasActiveFilters(hasFilters);
    
    // Clear the search term input since we're now using filter search
    setSearchTerm('');
  };

  const handleClearFilters = () => {
    setAppliedFilters(null);
    setHasActiveFilters(false);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const formatPrice = (value: number): string => {
    return formatCurrency(value, currencyCode, locale);
  };

  const fetchProducts = async (page = currentPage) => {
    try {
      setLoading(true);
      logger.log('Fetching products', { page, searchTerm, appliedFilters });
      
      const params = {
        page,
        perPage: pageSize,
        isAscending: false,
        // Use search from applied filters if available, otherwise use searchTerm
        ...(appliedFilters?.search ? { search: appliedFilters.search.trim() } : 
            searchTerm ? { search: searchTerm.trim() } : {}),
        // Apply all other filters from the filter component
        ...(appliedFilters?.category && { category: appliedFilters.category }),
        ...(appliedFilters?.customFieldFilter && { customFieldFilter: appliedFilters.customFieldFilter })
      };
      
      const response = await getAllProducts(params);
      logger.log('API Response:', response);
      
      if (response?.data) {
        const apiResponse = response.data;
        logger.log('API Response Data:', apiResponse);
        
        // Handle different possible response structures
        if (Array.isArray(apiResponse)) {
          // Direct array response
          setProducts(apiResponse);
          setTotalProducts(apiResponse.length);
        } else if (apiResponse.data && Array.isArray(apiResponse.data)) {
          // Nested data response
          setProducts(apiResponse.data);
          setTotalProducts(apiResponse.total || apiResponse.data.length);
        } else {
          // Fallback: ensure products is always an array
          logger.warn('Unexpected API response structure:', apiResponse);
          setProducts([]);
          setTotalProducts(0);
        }
      } else {
        // No data in response
        setProducts([]);
        setTotalProducts(0);
      }
    } catch (error) {
      console.error('Failed to load products', error);
      message.error('Failed to load products');
      // Ensure products is always an array even on error
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="main" className="main">
      <Header />
        <Card bodyStyle={{ padding: '16px' }}>
        <ContentHeader>
  <div className="title-section">
    <h1>Products ({totalProducts} items)</h1>
  </div>
  
  <div className="actions-section">
    <SearchContainer>
      <Search className="search-icon" size={16} />
      <Input
        placeholder="Search products..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </SearchContainer>
    
    {/* Add the BulkProductImport component here */}
    <BulkProductImport />
    
    {/* Simple Filter Dropdown */}
    <SimpleProductFilter
      onApplyFilter={handleApplyFilter}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
    />

    <Button
      onClick={() => {
        fetchProducts();
        message.success('Products refreshed');
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: '40px',
        padding: '0 12px',
        background: '#6366F1',
        borderColor: '#6366F1',
        color: 'white'
      }}
    >
      <RefreshCw size={16} />
      Refresh
    </Button>

    <Button
      type="primary"
      onClick={() => {
        logger.log("Add Product clicked - resetting selectedProduct to null");
        setSelectedProduct(null);
        setShowProductForm(true);
        logger.log("Form should open in CREATE mode, selectedProduct is now:", null);
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: '40px',
        padding: '0 16px'
      }}
    >
      <Plus size={16} />
      Add Product
    </Button>
  </div>
</ContentHeader>
         

          {!loading && products.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No Products Yet"
              description="Start building your product catalog by adding your first product. You can also import multiple products at once using the bulk import feature."
              actionLabel="Add Product"
              onAction={() => {
                logger.log("Add Product clicked from empty state");
                setSelectedProduct(null);
                setShowProductForm(true);
              }}
              secondaryActionLabel="Clear Filters"
              onSecondaryAction={hasActiveFilters ? handleClearFilters : undefined}
            />
          ) : (
          <ProductsGrid>
            {(products || []).map((product) => (
              <ProductCard
              key={product._id}
              onClick={() => handleEdit(product)} // Add this onClick handler to make whole card clickable
              style={{ cursor: 'pointer' }} // Make sure cursor is a pointer
            >
              <ActionButtons>
                <Button
                  type="text"
                  className="btn-icon"
                  onClick={(e) => {
                    e.stopPropagation(); // Add this to prevent the parent's onClick from being triggered
                    handleEdit(product);
                  }}
                  title="Edit product"
                >
                  <Edit2 size={14} />
                </Button>
                <Button
                  type="text"
                  className="btn-icon text-danger"
                  onClick={(e) => {
                    e.stopPropagation(); // Add this to prevent the parent's onClick from being triggered
                    setSelectedProduct(product);
                    setDeleteModalVisible(true);
                  }}
                  title="Delete product"
                >
                  <Trash2 size={14} />
                </Button>
              </ActionButtons>

                {product.images?.[1]?.filePath ? (
  <img
  src={`${bucketUrl.replace(/\/$/, '')}/${product.images[1].filePath.replace(/^\/+/, '')}`}
  alt={product.name}
  style={{
    width: '100%',
    height: '140px',
    objectFit: 'contain', // Changed from 'cover' to 'contain'
    objectPosition: 'center',
    borderRadius: '6px',
    backgroundColor: '#f8f9fa' // Light background to make products visible
  }}
  onError={(e) => {
    const failedSrc = e.currentTarget.src;
    console.error('Image load failed for S3 URL:', failedSrc);
    e.currentTarget.src = '/fallback.png';
  }}
/>
) : (
  <div style={{ 
    width: '100%', 
    height: '140px', 
    background: '#f8f9fa',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center' 
  }}>
    <Package size={24} color="#8392ab" />
  </div>
)}


<div style={{ padding: '8px' }}>
  <TruncatedProductName title={product.name}>
    {product.name}
  </TruncatedProductName>

  <TruncatedProductDescription title={product.category?.name || 'No Category'}>
    {product.category?.name || 'No Category'}
  </TruncatedProductDescription>
          
<div style={{ 
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
}}>
  <div style={{ 
    fontSize: '14px',
    fontWeight: 600,
    color: '#0284c7'
  }}>
    {formatCurrency(product.price, currencyCode, locale)}
  </div>
  {product?.unit && (
    <div style={{ 
      fontSize: '16px',
      color: '#64748b'
    }}>
      {product.unit}
    </div>
  )}
</div>
                </div>
              </ProductCard>
            ))}
          </ProductsGrid>
          )}

          {totalProducts > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <Pagination
                current={currentPage}
                total={totalProducts}
                pageSize={pageSize}
                onChange={setCurrentPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </Card>

        <ProductForm
  show={showProductForm}
  onHide={() => {
    logger.log("ProductList: Closing form");
    setShowProductForm(false);
    
    // Use setTimeout to ensure state updates happen after UI changes
    setTimeout(() => {
      setSelectedProduct(null);
      logger.log("ProductList: selectedProduct reset to null");
    }, 0);
  }}
  selectedProduct={selectedProduct}
  fetchProducts={fetchProducts}
/>

        <Modal
          title="Delete Product"
          open={deleteModalVisible}
          onOk={() => handleDelete(true)}
          onCancel={() => handleDelete(false)}
          className="custom-modal"
        >
          <p>Are you sure you want to delete this product?</p>
        </Modal>

    
    </div>
  );
};

export default ProductList;